import { Button } from './';

interface EventMetadata {
  startTime: Date;
  userRsvpStatus: 'yes' | 'no' | 'maybe' | null;
  rsvpCount: number;
  distanceKm?: number | null;
  registrationStatus: 'open' | 'closed' | 'full' | 'cancelled';
}

interface NextMoveCardProps {
  title: string;
  subtitle: string;
  time?: string; // Optional time display
  event?: EventMetadata; // Enhanced event data for metadata display
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  backgroundImage?: string;
  className?: string;
}

const NextMoveCard = ({
  title,
  subtitle,
  event,
  primaryAction,
  secondaryAction,
  backgroundImage,
  className = ''
}: NextMoveCardProps) => {
  // Helper function to format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Helper function to format distance
  const formatDistance = (km: number) => {
    if (km < 1) {
      return `${Math.round(km * 1000)}m`;
    }
    return `${km.toFixed(1)}km`;
  };

  // Get RSVP status indicator
  const getRsvpStatusIcon = (status: string | null, isUser: boolean = false) => {
    if (isUser && (status === 'yes' || status === 'maybe')) {
      return '✅'; // User has RSVP'd
    }
    return '👥'; // Generic people icon
  };

  // Get registration status
  const getRegistrationStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return '✅';
      case 'closed': return '🔒';
      case 'full': return '⚠️';
      case 'cancelled': return '❌';
      default: return '✅';
    }
  };

  return (
    <div 
      className={`relative overflow-hidden rounded-3xl p-5 ${className}`}
      style={{
        backgroundColor: 'var(--oro-surface-alt)',
        boxShadow: 'var(--shadow-floating)',
        border: '1px solid var(--oro-border)',
        ...(backgroundImage ? {
          background: `linear-gradient(135deg, rgba(12, 19, 16, 0.85) 0%, rgba(19, 26, 23, 0.9) 50%, rgba(26, 34, 31, 0.85) 100%), url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : {
          background: 'linear-gradient(135deg, var(--oro-surface) 0%, var(--oro-surface-alt) 50%, var(--oro-surface) 100%)'
        })
      }}
    >
      {/* Subtle topo overlay for texture */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
        <svg 
          width="100%" 
          height="100%" 
          viewBox="0 0 400 300" 
          className="w-full h-full"
        >
          <path 
            d="M0,100 Q100,80 200,100 T400,100"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
          <path 
            d="M0,150 Q150,130 300,150 T400,150"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
          <path 
            d="M0,200 Q100,220 200,200 T400,200"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
        </svg>
      </div>

      <div className="relative z-10 space-y-4">
        <div className="space-y-2">
          <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h2>
          <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
            {subtitle}
          </p>
        </div>

        {/* Event Metadata Icons */}
        {event && (
          <div className="flex flex-wrap gap-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
            {/* RSVP Count */}
            <div className="flex items-center gap-1.5">
              <span className="text-base">
                {getRsvpStatusIcon(event.userRsvpStatus, true)}
              </span>
              <span>{event.rsvpCount} Going</span>
            </div>

            {/* Event Time */}
            <div className="flex items-center gap-1.5">
              <span className="text-base">🕐</span>
              <span>{formatTime(event.startTime)}</span>
            </div>

            {/* Distance (if available) */}
            {event.distanceKm && (
              <div className="flex items-center gap-1.5">
                <span className="text-base">📍</span>
                <span>{formatDistance(event.distanceKm)}</span>
              </div>
            )}

            {/* Registration Status */}
            <div className="flex items-center gap-1.5">
              <span className="text-base">
                {getRegistrationStatusIcon(event.registrationStatus)}
              </span>
              <span className="capitalize">{event.registrationStatus}</span>
            </div>
          </div>
        )}

        {(primaryAction || secondaryAction) && (
          <div className="flex gap-3 pt-2">
            {primaryAction && (
              <Button
                variant="primary"
                onClick={primaryAction.onClick}
                className="transition-all hover:brightness-110"
                style={{
                  backgroundColor: 'var(--oro-gold)',
                  color: 'var(--oro-bg)',
                  border: 'none'
                }}
              >
                {primaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant="secondary"
                onClick={secondaryAction.onClick}
                className="transition-all"
                style={{
                  border: '1px solid var(--oro-border-hover)',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--oro-surface)'
                }}
              >
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NextMoveCard;